import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { WhatsappLogo, Phone, CalendarCheck, InstagramLogo, PaperPlaneTilt } from "@phosphor-icons/react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Reveal, SectionHead } from "./Reveal";
import { PHONE_TEL, WHATSAPP_URL, CALENDAR_URL, INSTAGRAM_URL } from "@/data/content";
import { useLanguage } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const METHOD_META = [
  { icon: WhatsappLogo, href: WHATSAPP_URL, testid: "contact-whatsapp-button" },
  { icon: Phone, href: PHONE_TEL, testid: "contact-call-button" },
  { icon: CalendarCheck, href: CALENDAR_URL, testid: "contact-calendar-button" },
  { icon: InstagramLogo, href: INSTAGRAM_URL, testid: "contact-instagram-button" },
];

const INTEREST_VALUES = ["Commercial", "Residential", "Other"];
const INTEREST_TESTIDS = ["interest-option-commercial", "interest-option-residential", "interest-option-other"];
const EMPTY = { name: "", email: "", phone: "", interest: "", message: "" };

export default function Contact() {
  const { t } = useLanguage();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2) return toast.error(t.contact.errors.name);
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) return toast.error(t.contact.errors.email);
    if (form.phone.trim().length < 5) return toast.error(t.contact.errors.phone);
    if (!form.interest) return toast.error(t.contact.errors.interest);
    if (form.message.trim().length < 5) return toast.error(t.contact.errors.message);

    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success(t.contact.success);
      setForm(EMPTY);
    } catch {
      toast.error(t.contact.failure);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="bg-[#0D0D0D] py-24 md:py-36">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <SectionHead index={t.contact.index} overline={t.contact.overline} title={t.contact.title} testid="contact" />
        <div className="mt-16 grid gap-16 lg:grid-cols-5">
          <div className="space-y-6 lg:col-span-2">
            {METHOD_META.map((m, i) => (
              <Reveal key={m.testid} delay={i * 0.1}>
                <a
                  data-testid={m.testid}
                  href={m.href}
                  target={m.href.startsWith("http") ? "_blank" : undefined}
                  rel="noopener noreferrer"
                  className="group flex items-center gap-5 border border-gold/15 bg-surface p-6 transition-all duration-500 hover:-translate-y-1 hover:border-gold/60 hover:shadow-lg hover:shadow-gold/5"
                >
                  <span className="border border-gold/30 p-3 text-gold transition-colors duration-300 group-hover:bg-gold group-hover:text-charcoal">
                    <m.icon size={26} weight="duotone" />
                  </span>
                  <span className="flex-1">
                    <span className="block font-serif text-xl tracking-wide text-[#FAFAFA]">{t.contact.methods[i].title}</span>
                    <span className="mt-1 block text-xs text-[#A3A3A3]">{t.contact.methods[i].note}</span>
                    <span className="mt-2 block text-[10px] uppercase tracking-[0.25em] text-gold">{t.contact.methods[i].cta} →</span>
                  </span>
                </a>
              </Reveal>
            ))}
            <Reveal delay={0.4}>
              <p className="border-l-2 border-gold/40 pl-4 text-sm italic leading-relaxed text-[#A3A3A3] rtl:border-l-0 rtl:border-r-2 rtl:pl-0 rtl:pr-4">
                {t.contact.promise}
              </p>
            </Reveal>
          </div>

          <Reveal delay={0.15} className="lg:col-span-3">
            <form data-testid="contact-form" onSubmit={handleSubmit} className="border border-gold/20 bg-surface p-8 md:p-10" noValidate>
              <div className="grid gap-6 md:grid-cols-2">
                <input data-testid="contact-name-input" className="lux-input" placeholder={t.contact.form.name} value={form.name} onChange={set("name")} />
                <input data-testid="contact-email-input" className="lux-input" type="email" placeholder={t.contact.form.email} value={form.email} onChange={set("email")} />
                <input data-testid="contact-phone-input" className="lux-input" type="tel" placeholder={t.contact.form.phone} value={form.phone} onChange={set("phone")} />
                <Select value={form.interest} onValueChange={(v) => setForm((f) => ({ ...f, interest: v }))}>
                  <SelectTrigger data-testid="contact-interest-select" className="lux-input h-auto border-0 border-b border-[#DAA520]/30 bg-[#111111] focus:ring-0 focus:ring-offset-0">
                    <SelectValue placeholder={t.contact.form.interest} />
                  </SelectTrigger>
                  <SelectContent className="border-gold/30 bg-[#111111] text-[#FAFAFA]">
                    {INTEREST_VALUES.map((v, i) => (
                      <SelectItem key={v} data-testid={INTEREST_TESTIDS[i]} value={v}>
                        {t.contact.form.interests[i]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <textarea
                data-testid="contact-message-input"
                className="lux-input mt-6 min-h-32 resize-y"
                placeholder={t.contact.form.message}
                value={form.message}
                onChange={set("message")}
              />
              <button
                data-testid="contact-submit-button"
                type="submit"
                disabled={loading}
                className="mt-8 flex w-full items-center justify-center gap-3 bg-gold px-8 py-4 text-xs font-semibold uppercase tracking-[0.3em] text-charcoal transition-colors duration-300 hover:bg-gold-bright disabled:opacity-60 md:w-auto"
              >
                {loading ? t.contact.form.sending : t.contact.form.submit} <PaperPlaneTilt size={16} weight="bold" />
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
